import querystring from 'query-string'
import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import { AppState } from '../stores/app'
import { axiosFetch } from '../config/axios-setup'
import { IServiceApiResponse } from '../interfaces/service-api-response'
import IApiCallOptions from '../interfaces/api-call-options'
import { DomainDto } from '../dtos/domain'
import aqe from '@pushly/aqe'
import { handleResponseErrorMessage } from '../_utils/response-error-utils'
import { ApiCallOptionsWithCustomErrorHandler } from '../types/api-call-options-with-custom-error-handler'
import type { Journey, JourneyRevision } from '../features/journeys/types/journey-api-response'

@Singleton
export class JourneyService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    protected get domain(): DomainDto {
        return this.appState.currentDomain!
    }

    public async fetchNextOccurrencesByQuery(query: any, opts?: IApiCallOptions): Promise<IServiceApiResponse<any[]>> {
        let ok = false
        let cancelled = false
        let occurrences: any[] = []
        let meta: any = {}

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const options = querystring.stringify(opts?.query ?? {})
            const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys/query-next-occurrences?${options}`

            const res = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: query,
                    config: {
                        cancelToken: opts?.cancellationToken,
                    },
                },
                opts?.cancellationKey,
            )
            const { data, ...metaData } = res.data

            ok = true
            occurrences = data
            meta = metaData
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, meta, data: occurrences }
    }

    public async fetchAll(opts?: IApiCallOptions): Promise<IServiceApiResponse<Journey[]>> {
        let ok = false
        let cancelled = false
        let journeys: Journey[] = []
        let meta: Record<string, unknown> = {}

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts?.query ?? {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v3/domains/${this.domain.id}/journeys?${options}`

        try {
            const res = await axiosFetch('get', { url: serviceURL, config: { skipCaseConversion: true } }, opts?.cancellationKey)
            const { data, ...metaData } = res.data

            ok = true
            journeys = data
            meta = metaData
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, meta, data: journeys }
    }

    public async fetchById(
        journeyId: number,
        opts?: ApiCallOptionsWithCustomErrorHandler,
    ): Promise<IServiceApiResponse<Journey | undefined>> {
        let ok = false
        let cancelled = false
        let journey: Journey | undefined

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const options = querystring.stringify(opts?.query ?? {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys/${journeyId}?${options}`

        try {
            const res = await axiosFetch('get', { url: serviceURL, config: { skipCaseConversion: true } }, opts?.cancellationKey)
            const { data } = res.data

            ok = true
            journey = data
        } catch (error) {
            const defaultHandler = () =>
                handleResponseErrorMessage(error, {
                    onCancelled: () => (cancelled = true),
                    fallbackMessage: 'An error has occurred',
                })

            if (opts?.errorHandler) {
                opts?.errorHandler(error, defaultHandler)
            } else {
                defaultHandler()
            }
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: journey }
    }

    public async create(journeyDto: Record<string, unknown>, opts?: IApiCallOptions): Promise<IServiceApiResponse<Journey | undefined>> {
        let ok = false
        let cancelled = false
        let journey: Journey | undefined

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys`

        try {
            const res = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: journeyDto,
                    config: { skipCaseConversion: true },
                },
                opts?.cancellationKey,
            )
            const { data } = res.data

            ok = true
            journey = data
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: journey }
    }

    public async patch(
        journeyId: number,
        journeyDto: Record<string, unknown>,
        opts?: IApiCallOptions,
    ): Promise<IServiceApiResponse<Journey | undefined>> {
        let ok = false
        let cancelled = false
        let journey: Journey | undefined

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys/${journeyId}`

        try {
            const res = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: journeyDto,
                    config: { skipCaseConversion: true },
                },
                opts?.cancellationKey,
            )
            const { data } = res.data

            ok = true
            journey = data
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: journey }
    }

    /**
     * Promote a winning split-test variant.
     *
     * POST /domains/:domainId/campaigns/:campaignId/steps/:stepId/promote/:branchKey
     *
     * Returns the updated journey on success.
     * Throws on 409 (already promoted / no active split / wrong status) or other 4xx/5xx.
     */
    public async promoteVariant(
        journeyId: number,
        stepId: number | string,
        branchKey: string,
        opts?: IApiCallOptions,
    ): Promise<IServiceApiResponse<Journey | undefined>> {
        let ok = false
        let cancelled = false
        let journey: Journey | undefined

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/campaigns/${journeyId}/steps/${stepId}/promote/${branchKey}`

        try {
            const res = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    config: { skipCaseConversion: true },
                },
                opts?.cancellationKey,
            )
            const { data } = res.data

            ok = true
            journey = data
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: journey }
    }

    /**
     * Fetch a specific revision's full step graph.
     *
     * GET /domains/:domainId/journeys/:journeyId/revisions/:revisionId
     *
     * Returns the JourneyRevision object (with steps) for that revision.
     * Used by the workflow tab's past-revision read-only viewer.
     */
    public async fetchRevisionSteps(
        journeyId: number,
        revisionId: number,
        opts?: IApiCallOptions,
    ): Promise<IServiceApiResponse<JourneyRevision | undefined>> {
        let ok = false
        let cancelled = false
        let revision: JourneyRevision | undefined

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const options = querystring.stringify(opts?.query ?? {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys/${journeyId}/revisions/${revisionId}?${options}`

        try {
            const res = await axiosFetch('get', { url: serviceURL, config: { skipCaseConversion: true } }, opts?.cancellationKey)
            const { data } = res.data

            ok = true
            // The revisions endpoint returns a full JourneyResponse: the requested revision —
            // the ONLY one carrying `steps` — is nested under `.revision`, while `revisions[]`
            // are step-less summaries. Extract `.revision` so callers receive a JourneyRevision
            // with steps (reading `data.steps` here would be undefined → empty read-only canvas).
            revision = data?.revision ?? data
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred loading revision details',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: revision }
    }

    public async destroy(journeyId: number, opts?: IApiCallOptions): Promise<IServiceApiResponse<void>> {
        let ok = false
        let cancelled = false

        if (opts?.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/domains/${this.domain.id}/journeys/${journeyId}`

        try {
            await axiosFetch(
                'delete',
                {
                    url: serviceURL,
                    config: { skipCaseConversion: true },
                },
                opts?.cancellationKey,
            )

            ok = true
        } catch (error) {
            handleResponseErrorMessage(error, {
                onCancelled: () => (cancelled = true),
                fallbackMessage: 'An error has occurred',
            })
        }

        if (opts?.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, cancelled, data: undefined }
    }
}
